import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, CircleAlert, FileText, PackageCheck, Plus, Truck, Wallet } from 'lucide-react'
import { listInvoices, listPOs, listReceipts } from '../../api/purchase'
import { getApAging } from '../../api/dashboard'
import Button from '../../components/Button'
import { Card, Tile } from '../../components/Summary'
import { companyNow, fmtDay, localToday, parseWallClock } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money, num } from '../sales/salesShared'
import PurchaseTabs from './PurchaseTabs'
import { invoiceState, poState, useVendorNames } from './purchaseShared'

// The Purchase summary, like the Sales one: what is on order, what has
// arrived, vendor invoices waiting on you, and what you owe.
export default function PurchaseMenu() {
  const { approvals } = useSession()
  const vendorNames = useVendorNames()
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancelled = false
    const quiet = (p, fallback) => p.catch(() => fallback)
    Promise.all([
      quiet(listPOs(), []),
      quiet(listReceipts(), []),
      quiet(listInvoices(), []),
      quiet(getApAging({ as_of: localToday(), limit: 5 }), null),
    ]).then(([pos, receipts, invoices, ap]) => {
      if (cancelled) return
      const arr = (v) => (Array.isArray(v) ? v : (v?.rows || []))
      setData({ pos: arr(pos), receipts: arr(receipts), invoices: arr(invoices), ap })
    })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="section-page">
      <PurchaseTabs />
      <header className="section-header">
        <h1>Purchase</h1>
        <div className="section-actions">
          {approvals.po > 0 && (
            <Link to="/purchase/po-approval-inbox" className="section-chip">
              <CircleAlert size={14} strokeWidth={2} aria-hidden="true" />
              {approvals.po} {approvals.po === 1 ? 'order' : 'orders'} to approve
            </Link>
          )}
          <Button as={Link} to="/purchase/receipts/new" variant="outline" size="sm"><PackageCheck size={14} />Receive goods</Button>
          <Button as={Link} to="/purchase/invoices/new" variant="outline" size="sm"><FileText size={14} />New vendor invoice</Button>
          <Button as={Link} to="/purchase/po-form/new" variant="primary" size="sm"><Plus size={14} />New purchase order</Button>
        </div>
      </header>

      {data ? <Summary {...data} vendorNames={vendorNames} /> : <p className="sum-state">Loading purchases…</p>}
    </div>
  )
}

function Summary({ pos, receipts, invoices, ap, vendorNames }) {
  const now = companyNow()
  const month = now.toLocaleDateString(undefined, { month: 'long' })
  const inMonth = (v) => {
    const d = parseWallClock(v)
    return d && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  }
  const byDate = (field) => (a, b) => (parseWallClock(b[field])?.getTime() || 0) - (parseWallClock(a[field])?.getTime() || 0)

  const waiting = pos.filter((p) => poState(p).id === 'ordered').sort((a, b) => -byDate('po_date')(a, b))
  const waitingValue = waiting.reduce((s, p) => s + num(p.total_amount), 0)
  const orderedThisMonth = pos.filter((p) => inMonth(p.po_date) && poState(p).id !== 'rejected')
  const needsYou = invoices.filter((i) => invoiceState(i).group === 'yours')
  const withAccounting = invoices.filter((i) => invoiceState(i).group === 'accounting')
  const recent = [...receipts].sort(byDate('receipt_date')).slice(0, 6)
  const owed = num(ap?.totals?.balance)
  const pastDue = Math.max(0, owed - num(ap?.totals?.current))

  return (
    <>
      <div className="sum-tiles">
        <Tile label="Waiting for goods" href="/purchase/po-list?show=ordered" icon={Truck} value={String(waiting.length)}
          note={waiting.length ? `Worth ${money(waitingValue)}` : 'Nothing on order'} />
        <Tile label={`Ordered in ${month}`} href="/purchase/po-list" icon={CalendarDays}
          value={money(orderedThisMonth.reduce((s, p) => s + num(p.total_amount), 0))}
          note={`${orderedThisMonth.length} ${orderedThisMonth.length === 1 ? 'order' : 'orders'}`} />
        <Tile label="Vendor invoices to finish" href="/purchase/invoices?show=yours" icon={FileText} value={String(needsYou.length)}
          note={withAccounting.length ? `${withAccounting.length} with Accounting` : 'None with Accounting'}
          tone={needsYou.some((i) => invoiceState(i).tone === 'warn') ? 'warn' : undefined} />
        <Tile label="Owed to vendors" href="/purchase/ap-ledger" icon={Wallet} value={money(owed)}
          note={pastDue > 0 ? `${money(pastDue)} past due` : 'Nothing past due'} tone={pastDue > 0 ? 'warn' : undefined} />
      </div>

      <div className="sum-columns">
        <div className="sum-main">
          <Card title="Waiting for goods" action={<Link to="/purchase/po-list?show=ordered">All orders</Link>}>
            {waiting.length ? (
              <ul className="sum-list">
                {waiting.slice(0, 6).map((p) => (
                  <li key={p.po_id}>
                    <span className="sum-list-main">
                      <Link to={`/purchase/po-detail/${p.po_id}`}>{docNo('po', p.po_no)}</Link>
                      <span className="sum-muted"> · {p.vendor_name || vendorNames[String(p.vendor_id)] || 'Vendor'} · ordered {fmtDay(p.po_date, { year: false })}</span>
                    </span>
                    <span className="sum-list-side">
                      <Link to={`/purchase/receipts/new?po_id=${p.po_id}`}>Receive</Link>
                      <span className="sum-muted"> · </span>{money(p.total_amount)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">Nothing is on order.</p>
            )}
          </Card>

          <Card title="Recent deliveries" action={<Link to="/purchase/receipts">All receiving</Link>}>
            {recent.length ? (
              <ul className="sum-list">
                {recent.map((r) => (
                  <li key={r.receipt_id}>
                    <span className="sum-list-main">
                      <Link to={`/purchase/receipts/${r.receipt_id}`}>{docNo('receipt', r.receipt_no)}</Link>
                      <span className="sum-muted"> · {vendorNames[String(r.vendor_id)] || 'Vendor'}{r.po_no ? ` · ${docNo('po', r.po_no)}` : ''}</span>
                    </span>
                    <span className="sum-list-side">
                      <span className="sum-muted">{fmtDay(r.receipt_date, { year: false })} · </span>
                      {num(r.line_count)} {num(r.line_count) === 1 ? 'item' : 'items'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">Nothing received yet.</p>
            )}
          </Card>
        </div>

        <div className="sum-side">
          <Card title="Vendor invoices to finish" action={<Link to="/purchase/invoices">All vendor invoices</Link>}>
            {needsYou.length ? (
              <ul className="sum-list">
                {needsYou.slice(0, 5).map((i) => {
                  const st = invoiceState(i)
                  return (
                    <li key={i.invoice_id}>
                      <span className="sum-list-main">
                        <Link to={`/purchase/invoices/${i.invoice_id}`}>{i.invoice_number || docNo('bill', i.bill_no)}</Link>
                        <span className={st.tone === 'warn' ? 'sum-warn' : 'sum-muted'}> · {i.vendor_name || 'Vendor'} · {st.label.toLowerCase()}</span>
                      </span>
                      <span className="sum-list-side">{money(i.total_amount)}</span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="sum-muted">Nothing is waiting on you.</p>
            )}
          </Card>

          <Card title="Owed to vendors" action={<Link to="/purchase/ap-ledger">Pay a vendor</Link>}>
            {ap?.vendors?.length ? (
              <ul className="sum-list">
                {ap.vendors.map((v) => {
                  const due = Math.max(0, num(v.balance) - num(v.current))
                  return (
                    <li key={v.vendor_id ?? v.vendor_name}>
                      <span className="sum-list-main">
                        {v.vendor_id ? <Link to={`/purchase/ap/vendor/${v.vendor_id}`}>{v.vendor_name || `Vendor #${v.vendor_id}`}</Link> : v.vendor_name}
                        {due > 0 && <span className="sum-warn"> · {money(due)} past due</span>}
                      </span>
                      <span className="sum-list-side">{money(v.balance)}</span>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="sum-muted">You don’t owe any vendors.</p>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}
